import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslocoPipe } from '@jsverse/transloco';
import { F } from '@mobily/ts-belt';
import { FilterPipe, PanelComponent, SelectSearchBarComponent, Str } from '@web/birdz-angular';
import { Database } from 'app/interfaces/database.interface';
import { QueryFilter } from 'app/interfaces/query-filter.interface';
import { QueryService } from 'app/services/query.service';
import { combineLatest, distinctUntilChanged, tap } from 'rxjs';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatTabsModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatRadioModule,
    MatButtonModule,
    PanelComponent,
    TranslocoPipe,
    FilterPipe,
    SelectSearchBarComponent,
  ],
})
export class FilterComponent implements OnInit {
  private queryService = inject(QueryService);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  databases: Database[] = [];
  creators: string[] = [];
  tags: string[] = [];
  isApplied = true;
  selectedTab = 0;

  dbOptionToLabel = (db: Database) => db.label;
  searchDbFn = (search: string, db: Database) => Str.search(search, db.label);
  searchStringFn = (search: string, value: string) => Str.search(search, value);

  form: FormGroup = this.fb.group({
    name: [null],
    database_id: [null],
    creator_email: [null],
    tags: [[]],
    table_name: [null],
    favorites_only: [false],
    show_active: [true],
    show_deleted: [false],
    only_private: [false],
  });

  ngOnInit(): void {
    this.loadOptions();
    this.form.patchValue(this.queryService.currentFilter$.value, { emitEvent: false });
    this.initIsApplied();
  }

  private loadOptions(): void {
    this.queryService
      .getDatabases()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dbs) => (this.databases = dbs));
    this.queryService
      .getCreators()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((c) => (this.creators = c));
    this.queryService.tags$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((t) => (this.tags = t));
    this.queryService.refreshTags();
  }

  private initIsApplied(): void {
    combineLatest([this.queryService.currentFilter$, this.form.valueChanges])
      .pipe(
        distinctUntilChanged(),
        tap(([applied, form]) => (this.isApplied = F.equals(applied, form))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  reset(): void {
    const empty: QueryFilter = {
      name: null,
      database_id: null,
      creator_email: null,
      tags: [],
      table_name: null,
      favorites_only: false,
      sort_by: this.queryService.currentFilter$.value.sort_by,
      show_active: true,
      show_deleted: false,
      only_private: false,
    };
    this.form.reset(empty);
    this.queryService.applyFilter(empty);
  }

  submit(): void {
    this.queryService.applyFilter({
      ...this.form.value,
      sort_by: this.queryService.currentFilter$.value.sort_by,
    } as QueryFilter);
  }
}
